import * as THREE from "three";
import { schemaParts, type FigureCard, type Schema, type Vec3 } from "./card";
import type { Figure } from "./build";

// The pose layer (spike 002, phase B: action poses and hit reactions): named poses as data, keyed by time, played on
// top of the procedural motion. A pose is joint turns (Euler XYZ, radians, in each joint's parent frame) at keys
// along its segments, plus an optional root channel (an offset and a turn of the whole figure about a pivot point:
// a dodge roll, a stagger's step back). Each play is an entry on a track: plain data (times in the game's sim
// seconds), so a game keeps tracks in its sim and saves them, the view samples them at the frame's time, and a
// ragdoll can be seeded from the same sample at a sim step (deterministic).
//
// - Segments: a pose has one or more segments, each with its seconds (`beats`). An unphased pose runs through them
//   and ends. A phased pose names its segments (`phases`, e.g. windup, active, recovery) and holds at the end of each
//   until told the next (`phasePose`): the attack-poses-keyed-to-the-hit-window suggestion, so the strike key lands
//   exactly when the move's active window opens whatever the move's timing, and retiming a move retimes its pose.
//   The last segment ends the pose on its own.
// - Blending: an entry blends in over `blend.in` seconds and out over `blend.out` (when stopped, or before its end).
//   Override poses: starting one stops the others, which blend out under it; their turns are averaged by weight and
//   slerped from the motion's. Additive poses (a flinch) multiply onto whatever is there, each by its weight.
// - Masks: "full" (every joint and the root channel) or "upper" (the trunk joint `body` and every joint under it: legs
//   keep the gait). A play may override the pose's own mask.
// - Direction: an entry's `dir` turns the whole pose about +y in the body frame (each key's turn conjugated, the root
//   offset rotated): a reaction authored for a blow from the front leans the right way for a blow from any side.
//
// Planted defects (`POSE_FAULTS`) beside what they break: `maskIgnored` (an upper-body pose turns the legs too) and
// `undirected` (`dir` ignored: every reaction leans as if hit from the front).

/** Planted defects: the pose-layer unit tests and the hit-reaction spec must fail with them on. */
export const POSE_FAULTS = {
  /** Masks are ignored: every keyed joint turns, the legs under an upper-body pose included. */
  maskIgnored: false,
  /** An entry's direction is ignored: reactions all lean as if the blow came from the front. */
  undirected: false,
};

/** Which joints a pose may turn: every joint and the root channel, or the trunk (`body`) and its subtree. */
export type PoseMask = "full" | "upper";

/** One key: joint turns (Euler XYZ, radians) and the root channel, at a time in beats (segment i spans [i, i + 1]). */
export interface PoseKey {
  at: number;
  joints?: Record<string, Vec3>;
  root?: { offset?: Vec3; rot?: Vec3 };
}

/** A named pose as data. */
export interface ActionPose {
  id: string;
  mask: PoseMask;
  /** Multiplied onto the pose under it (a flinch) instead of replacing it. */
  additive?: boolean;
  /** Seconds per segment (1 to 6 segments). */
  beats: number[];
  /** Names for the segments: the pose holds at each segment's end until `phasePose` moves it on. Absent: it runs. */
  phases?: string[];
  /** Seconds to blend in and out. */
  blend: { in: number; out: number };
  /** The model-space point the root channel turns about (default [0, 0.5, 0]). */
  pivot?: Vec3;
  /** Show the arms while it plays (the motion's `acting`). */
  arms?: boolean;
  keys: PoseKey[];
}

/** One play of a pose on a track. Plain data; times in the track's clock (the game's sim seconds). */
export interface PoseEntry {
  pose: string;
  /** When it started (its blend in). */
  start: number;
  /** The segment it is in, and when that segment started. */
  segment: number;
  segStart: number;
  /** When it was told to stop (its blend out), or null. */
  stop: number | null;
  /** 0 to 1. */
  weight: number;
  /** Turn about +y in the body frame (radians) applied to the whole pose. */
  dir: number;
  /** Segment-time multiplier (2: twice as fast). */
  speed: number;
  mask: PoseMask;
}

/** A figure's plays, newest last. Plain data: keep it in the sim, save it, sample it anywhere. */
export interface PoseTrack {
  entries: PoseEntry[];
}

/** What a play may set. */
export interface PlayOptions {
  weight?: number;
  dir?: number;
  speed?: number;
  /** Start at this segment (a phase index). */
  segment?: number;
  mask?: PoseMask;
}

interface Channel {
  at: number[];
  q: THREE.Quaternion[];
}
interface CompiledPose {
  pose: ActionPose;
  joints: Map<string, Channel>;
  offset: { at: number[]; v: THREE.Vector3[] } | null;
  rot: Channel | null;
  pivot: THREE.Vector3;
  total: number;
}

/** A set of poses, checked and compiled to channels. */
export interface PoseLibrary {
  has(id: string): boolean;
  get(id: string): ActionPose;
  ids(): string[];
  /** Internal: the compiled pose. */
  compiled(id: string): CompiledPose;
}

const euler = new THREE.Euler();
const quatOf = (v: Vec3): THREE.Quaternion => new THREE.Quaternion().setFromEuler(euler.set(v[0], v[1], v[2], "XYZ"));

/** Check and compile poses. Throws naming the pose and what is wrong (keys out of order or past its segments, a phase
 *  list of the wrong length, a duplicate id). */
export function createPoseLibrary(poses: readonly ActionPose[]): PoseLibrary {
  const map = new Map<string, CompiledPose>();
  for (const pose of poses) {
    const bad = (why: string): never => {
      throw new Error(`@rpg/figure: pose "${pose.id}" ${why}`);
    };
    if (map.has(pose.id)) bad("is defined twice");
    if (!pose.beats.length || pose.beats.some((b) => !(b > 0))) bad("needs one or more segments, each longer than 0 s");
    if (pose.phases && pose.phases.length !== pose.beats.length) bad(`names ${pose.phases.length} phases for ${pose.beats.length} segments`);
    if (!pose.keys.length) bad("has no keys");
    const n = pose.beats.length;
    let last = -Infinity;
    for (const k of pose.keys) {
      if (!(k.at >= 0 && k.at <= n)) bad(`has a key at ${k.at}, outside its ${n} segment(s)`);
      if (k.at < last) bad("has keys out of order");
      last = k.at;
    }
    const joints = new Map<string, Channel>();
    let offset: CompiledPose["offset"] = null;
    let rot: Channel | null = null;
    for (const k of pose.keys) {
      for (const [j, e] of Object.entries(k.joints ?? {})) {
        const c = joints.get(j) ?? { at: [], q: [] };
        c.at.push(k.at);
        c.q.push(quatOf(e));
        joints.set(j, c);
      }
      if (k.root?.offset) {
        offset ??= { at: [], v: [] };
        offset.at.push(k.at);
        offset.v.push(new THREE.Vector3(...k.root.offset));
      }
      if (k.root?.rot) {
        rot ??= { at: [], q: [] };
        rot.at.push(k.at);
        rot.q.push(quatOf(k.root.rot));
      }
    }
    // consecutive keys take the shorter way: keep each quaternion on the previous one's side
    for (const c of [...joints.values(), ...(rot ? [rot] : [])]) for (let i = 1; i < c.q.length; i++) if (c.q[i]!.dot(c.q[i - 1]!) < 0) c.q[i]!.set(-c.q[i]!.x, -c.q[i]!.y, -c.q[i]!.z, -c.q[i]!.w);
    map.set(pose.id, { pose, joints, offset, rot, pivot: new THREE.Vector3(...(pose.pivot ?? [0, 0.5, 0])), total: pose.beats.reduce((a, b) => a + b, 0) });
  }
  const get = (id: string): CompiledPose => {
    const c = map.get(id);
    if (!c) throw new Error(`@rpg/figure: no pose "${id}"`);
    return c;
  };
  return { has: (id) => map.has(id), get: (id) => get(id).pose, ids: () => [...map.keys()], compiled: get };
}

const smooth = (u: number): number => u * u * (3 - 2 * u);
const clamp01 = (u: number): number => Math.min(1, Math.max(0, u));

/** Find the bracketing keys for beat b in a sorted list: [i0, i1, u]. */
function bracket(at: readonly number[], b: number): [number, number, number] {
  if (b <= at[0]!) return [0, 0, 0];
  const last = at.length - 1;
  if (b >= at[last]!) return [last, last, 0];
  let i = 0;
  while (i < last && at[i + 1]! < b) i++;
  const span = at[i + 1]! - at[i]!;
  return [i, i + 1, span > 0 ? smooth((b - at[i]!) / span) : 1];
}

function channelAt(c: Channel, b: number, out: THREE.Quaternion): THREE.Quaternion {
  const [i0, i1, u] = bracket(c.at, b);
  return out.copy(c.q[i0]!).slerp(c.q[i1]!, u);
}

/** The beat (segment + progress) an entry is at, at time `now`. */
export function entryBeat(e: PoseEntry, pose: ActionPose, now: number): number {
  const beats = pose.beats;
  let el = Math.max(0, now - e.segStart);
  const speed = e.speed > 0 ? e.speed : 1;
  if (pose.phases && e.segment < beats.length - 1) return e.segment + clamp01(el / (beats[e.segment]! / speed));
  for (let s = e.segment; s < beats.length; s++) {
    const d = beats[s]! / speed;
    if (el < d) return s + el / d;
    el -= d;
  }
  return beats.length;
}

/** When an entry ends on its own (its last segment done), or Infinity while a phased pose waits on a phase. */
export function entryEnd(e: PoseEntry, pose: ActionPose): number {
  const beats = pose.beats;
  if (pose.phases && e.segment < beats.length - 1) return Infinity;
  const speed = e.speed > 0 ? e.speed : 1;
  let t = e.segStart;
  for (let s = e.segment; s < beats.length; s++) t += beats[s]! / speed;
  return t;
}

/** An entry's weight now: its own, eased in from its start, eased out after a stop or before its end. */
export function entryWeight(e: PoseEntry, pose: ActionPose, now: number): number {
  const wIn = pose.blend.in > 0 ? clamp01((now - e.start) / pose.blend.in) : now >= e.start ? 1 : 0;
  const out = Math.max(1e-6, pose.blend.out);
  const fromStop = e.stop === null ? 1 : clamp01(1 - (now - e.stop) / out);
  const end = entryEnd(e, pose);
  const fromEnd = end === Infinity ? 1 : clamp01((end - now) / out);
  return e.weight * smooth(wIn) * smooth(Math.min(fromStop, fromEnd));
}

/** An empty track. */
export function poseTrack(): PoseTrack {
  return { entries: [] };
}

/** Start a pose at `now`. An override pose stops every other override (they blend out under it); additive poses stack.
 *  Returns the new entry. Throws for an unknown pose. */
export function playPose(track: PoseTrack, lib: PoseLibrary, id: string, now: number, o: PlayOptions = {}): PoseEntry {
  const pose = lib.get(id);
  if (!pose.additive) for (const e of track.entries) if (e.stop === null && !lib.get(e.pose).additive) e.stop = now;
  const segment = Math.max(0, Math.min(pose.beats.length - 1, Math.round(o.segment ?? 0)));
  const entry: PoseEntry = { pose: id, start: now, segment, segStart: now, stop: null, weight: clamp01(o.weight ?? 1), dir: o.dir ?? 0, speed: o.speed ?? 1, mask: o.mask ?? pose.mask };
  track.entries.push(entry);
  // keep the track small: the oldest stopped entries go first
  while (track.entries.length > 8) {
    const i = track.entries.findIndex((e) => e.stop !== null);
    track.entries.splice(i >= 0 ? i : 0, 1);
  }
  return entry;
}

/** Move a phased pose to a phase (a name from its `phases`, or a segment index) at `now`; starts it there when it
 *  isn't playing. */
export function phasePose(track: PoseTrack, lib: PoseLibrary, id: string, phase: string | number, now: number, o: PlayOptions = {}): PoseEntry {
  const pose = lib.get(id);
  const segment = typeof phase === "number" ? phase : (pose.phases ?? []).indexOf(phase);
  if (segment < 0 || segment >= pose.beats.length) throw new Error(`@rpg/figure: pose "${id}" has no phase "${phase}"`);
  const e = [...track.entries].reverse().find((x) => x.pose === id && x.stop === null);
  if (!e) return playPose(track, lib, id, now, { ...o, segment });
  e.segment = segment;
  e.segStart = now;
  return e;
}

/** Stop a pose's playing entries at `now` (they blend out). */
export function stopPose(track: PoseTrack, id: string, now: number): void {
  for (const e of track.entries) if (e.pose === id && e.stop === null) e.stop = now;
}

/** Drop the entries that have blended out or ended. */
export function prunePoses(track: PoseTrack, lib: PoseLibrary, now: number): void {
  track.entries = track.entries.filter((e) => {
    const pose = lib.get(e.pose);
    if (e.stop !== null && now - e.stop >= pose.blend.out) return false;
    return now < entryEnd(e, pose);
  });
}

/** What the layer does to one joint: an override turn and its weight (null: none), then an additive turn. */
export interface JointOp {
  set: THREE.Quaternion | null;
  setWeight: number;
  add: THREE.Quaternion;
}

/** A track sampled at one time: per-joint operations, the root channel and whether the arms show. */
export interface PoseSample {
  joints: Map<string, JointOp>;
  root: { offset: THREE.Vector3; rot: THREE.Quaternion; pivot: THREE.Vector3 };
  arms: boolean;
  /** The poses contributing, with their weights. */
  active: { pose: string; weight: number; beat: number }[];
}

/** The joints the "upper" mask covers: `body` and everything under it (every joint when there is no `body`). */
export function upperJoints(card: Pick<FigureCard, "joints">): Set<string> {
  const parent = new Map(card.joints.map((j) => [j.id, j.parent]));
  if (!parent.has("body")) return new Set(parent.keys());
  const out = new Set<string>();
  for (const id of parent.keys()) {
    for (let c: string | undefined = id; c; c = parent.get(c))
      if (c === "body") {
        out.add(id);
        break;
      }
  }
  return out;
}

const _q = new THREE.Quaternion();
const _q2 = new THREE.Quaternion();
const _turn = new THREE.Quaternion();
const _turnInv = new THREE.Quaternion();
const UP = new THREE.Vector3(0, 1, 0);

/** Sample a track at `now`: what each joint and the root get. `upper` is the figure's upper-body joint set. */
export function samplePoses(track: PoseTrack, lib: PoseLibrary, now: number, upper: ReadonlySet<string>): PoseSample {
  const sums = new Map<string, { x: number; y: number; z: number; w: number; total: number; keep: number; ref: THREE.Quaternion | null }>();
  const adds = new Map<string, THREE.Quaternion>();
  const sample: PoseSample = { joints: new Map(), root: { offset: new THREE.Vector3(), rot: new THREE.Quaternion(), pivot: new THREE.Vector3(0, 0.5, 0) }, arms: false, active: [] };
  // root overrides: a weighted average (offset, turn, pivot) and the chance of none, as for the joints
  const root = { off: new THREE.Vector3(), rot: { x: 0, y: 0, z: 0, w: 0 }, ref: null as THREE.Quaternion | null, pivot: new THREE.Vector3(), total: 0, keep: 1 };
  const rootAdd = { off: new THREE.Vector3(), rot: new THREE.Quaternion() };
  for (const e of track.entries) {
    const c = lib.compiled(e.pose);
    const w = entryWeight(e, c.pose, now);
    if (w <= 1e-6) continue;
    const beat = entryBeat(e, c.pose, now);
    sample.active.push({ pose: e.pose, weight: w, beat });
    if (c.pose.arms) sample.arms = true;
    const dir = POSE_FAULTS.undirected ? 0 : e.dir;
    _turn.setFromAxisAngle(UP, dir);
    _turnInv.copy(_turn).invert();
    const full = POSE_FAULTS.maskIgnored || e.mask === "full";
    for (const [j, ch] of c.joints) {
      if (!full && !upper.has(j)) continue;
      const q = channelAt(ch, beat, new THREE.Quaternion());
      if (dir !== 0) q.premultiply(_turn).multiply(_turnInv);
      if (c.pose.additive) {
        const a = adds.get(j) ?? new THREE.Quaternion();
        adds.set(j, a.multiply(_q.identity().slerp(q, w)));
      } else {
        const s = sums.get(j) ?? { x: 0, y: 0, z: 0, w: 0, total: 0, keep: 1, ref: null };
        // a weighted average on one hemisphere, and the chance of no override at all (∏ (1 − w))
        if (s.ref && s.ref.dot(q) < 0) q.set(-q.x, -q.y, -q.z, -q.w);
        s.ref ??= q.clone();
        s.x += q.x * w;
        s.y += q.y * w;
        s.z += q.z * w;
        s.w += q.w * w;
        s.total += w;
        s.keep *= 1 - Math.min(1, w);
        sums.set(j, s);
      }
    }
    if (full && (c.offset || c.rot)) {
      const off = new THREE.Vector3();
      if (c.offset) {
        const [i0, i1, u] = bracket(c.offset.at, beat);
        off.copy(c.offset.v[i0]!).lerp(c.offset.v[i1]!, u);
      }
      if (dir !== 0) off.applyQuaternion(_turn);
      const rot = c.rot ? channelAt(c.rot, beat, new THREE.Quaternion()) : new THREE.Quaternion();
      if (dir !== 0) rot.premultiply(_turn).multiply(_turnInv);
      if (c.pose.additive) {
        rootAdd.off.addScaledVector(off, w);
        rootAdd.rot.multiply(_q2.identity().slerp(rot, w));
      } else {
        if (root.ref && root.ref.dot(rot) < 0) rot.set(-rot.x, -rot.y, -rot.z, -rot.w);
        root.ref ??= rot.clone();
        root.off.addScaledVector(off, w);
        root.rot.x += rot.x * w;
        root.rot.y += rot.y * w;
        root.rot.z += rot.z * w;
        root.rot.w += rot.w * w;
        root.pivot.addScaledVector(c.pivot, w);
        root.total += w;
        root.keep *= 1 - Math.min(1, w);
      }
    }
  }
  if (root.total > 0) {
    const k = 1 - root.keep;
    sample.root.offset.copy(root.off).multiplyScalar(k / root.total);
    sample.root.rot.slerp(_q.set(root.rot.x, root.rot.y, root.rot.z, root.rot.w).normalize(), k);
    sample.root.pivot.copy(root.pivot).multiplyScalar(1 / root.total);
  }
  sample.root.offset.add(rootAdd.off);
  sample.root.rot.multiply(rootAdd.rot);
  for (const [j, s] of sums) {
    const q = new THREE.Quaternion(s.x, s.y, s.z, s.w).normalize();
    sample.joints.set(j, { set: q, setWeight: 1 - s.keep, add: adds.get(j) ?? new THREE.Quaternion() });
  }
  for (const [j, a] of adds) if (!sample.joints.has(j)) sample.joints.set(j, { set: null, setWeight: 0, add: a });
  return sample;
}

/** A joint's local turn after the layer: the base (the motion's turn, or rest) slerped toward the override by its
 *  weight, then the additive turn. */
export function layeredTurn(base: THREE.Quaternion, op: JointOp | undefined, out = new THREE.Quaternion()): THREE.Quaternion {
  out.copy(base);
  if (!op) return out;
  if (op.set && op.setWeight > 0) out.slerp(op.set, Math.min(1, op.setWeight));
  return out.multiply(op.add);
}

/** Put every joint's turn back to rest. A game with a pose layer calls it before the motion poses a frame: the motion
 *  sets only the turns it drives (a lean about x, aimed legs), so a layer's turn from the last frame would otherwise
 *  stay under it and build up. */
export function resetTurns(fig: Figure): void {
  for (const j of fig.joints.values()) j.quaternion.identity();
}

/** Put a sample on a figure posed by its motion this frame: each joint's turn layered, the root channel on the pivot
 *  (identity when the sample has none, so the pivot never keeps an old pose), and the spring chains re-pinned to their
 *  turned anchors. */
export function applyPoses(fig: Figure, sample: PoseSample): void {
  for (const [id, op] of sample.joints) {
    const j = fig.joints.get(id);
    if (j) layeredTurn(j.quaternion, op, j.quaternion);
  }
  const r = sample.root;
  const pivot = _v.copy(r.pivot).multiplyScalar(fig.scale);
  fig.pivot.quaternion.copy(r.rot);
  // turn about the pivot point: p' = offset + pivot − R·pivot
  fig.pivot.position.copy(r.offset).multiplyScalar(fig.scale).add(pivot).sub(_v2.copy(pivot).applyQuaternion(r.rot));
  fig.root.updateMatrixWorld(true);
  // the motion stepped the spring chains before the layer turned their anchors: pin them to where the anchors are now
  for (const c of fig.chains.values()) c.update(0);
}
const _v = new THREE.Vector3();
const _v2 = new THREE.Vector3();

/** A pose layer over one figure: a track, a library and the figure's masks, for a game that keeps the track in its
 *  view (a game that keeps it in the sim uses the functions above with its own track). */
export interface PoseLayer {
  track: PoseTrack;
  readonly library: PoseLibrary;
  play(id: string, now: number, o?: PlayOptions): PoseEntry;
  phase(id: string, phase: string | number, now: number, o?: PlayOptions): PoseEntry;
  stop(id: string, now: number): void;
  /** Sample at `now`, put it on the figure (after the motion's update; call `resetTurns` before that update), prune
   *  what has ended. */
  apply(now: number): PoseSample;
  /** Pose the figure at pose `id`'s time `t` (seconds from its start, through its segments), full weight, now:
   *  for shots and tools. Clears the track. */
  actionPose(id: string, t: number): PoseSample;
}

/** Make a pose layer for a figure. */
export function createPoseLayer(fig: Figure, library: PoseLibrary): PoseLayer {
  const upper = upperJoints(fig.card);
  const layer: PoseLayer = {
    track: poseTrack(),
    library,
    play: (id, now, o) => playPose(layer.track, library, id, now, o),
    phase: (id, phase, now, o) => phasePose(layer.track, library, id, phase, now, o),
    stop: (id, now) => stopPose(layer.track, id, now),
    apply(now) {
      const s = samplePoses(layer.track, library, now, upper);
      applyPoses(fig, s);
      prunePoses(layer.track, library, now);
      return s;
    },
    actionPose(id, t) {
      const pose = library.get(id);
      // an unphased, unblended stand-in entry at time t
      const held: ActionPose = { ...pose, phases: undefined, blend: { in: 0, out: 1e-6 } };
      const lib = createPoseLibrary([held]);
      const track: PoseTrack = { entries: [{ pose: id, start: -1, segment: 0, segStart: 0, stop: null, weight: 1, dir: 0, speed: 1, mask: pose.mask }] };
      const s = samplePoses(track, lib, Math.min(t, lib.compiled(id).total - 1e-6), upper);
      layer.track = poseTrack();
      applyPoses(fig, s);
      return s;
    },
  };
  return layer;
}

// ---------------------------------------------------------------------------------------------------------------
// The schema (draft-07): a pose as content.

const { num, obj, list, vec, JOINT_ID, TAU } = schemaParts;

const KEY = obj(
  {
    at: num(0, 6, "Beats: segment i spans [i, i + 1]"),
    joints: { type: "object", propertyNames: JOINT_ID, additionalProperties: vec(-TAU, TAU), description: "Joint turns, Euler XYZ radians, parent frame" },
    root: obj({ offset: vec(-2, 2, "Metres, body frame"), rot: vec(-TAU, TAU, "Euler XYZ radians about the pivot") }, []),
  },
  ["at"],
);

/** The action pose's JSON Schema; `extra` adds the game's fields (as for `figureCardSchema`). */
export function actionPoseSchema(extra: { properties?: Record<string, Schema>; required?: string[]; title?: string; description?: string } = {}): Schema {
  return {
    $schema: "http://json-schema.org/draft-07/schema#",
    title: extra.title ?? "ActionPose",
    ...(extra.description ? { description: extra.description } : {}),
    type: "object",
    additionalProperties: false,
    required: [...(extra.required ?? []), "id", "mask", "beats", "blend", "keys"],
    properties: {
      ...(extra.properties ?? {}),
      id: { type: "string" },
      mask: { enum: ["full", "upper"] },
      additive: { type: "boolean" },
      beats: list(num(0.01, 5, "Seconds"), 1, 6, "Seconds per segment"),
      phases: list({ type: "string" }, 1, 6, "Segment names; the pose holds at each segment's end until the next phase"),
      blend: obj({ in: num(0, 2), out: num(0, 2) }, ["in", "out"]),
      pivot: vec(-2, 3, "Model-space point the root channel turns about"),
      arms: { type: "boolean" },
      keys: list(KEY, 1, 32),
    },
  };
}
